import { useEffect, useMemo, useRef, useState } from "react";
import { GlBasemap } from "../lib/basemap";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { ArrowRight, MapPin, MagnifyingGlass, X } from "@phosphor-icons/react";
import { useTheme } from "../lib/theme";
import api from "../lib/api";

// Default geographic frame — Lancaster County, PA. Used when the API has
// no pins yet (e.g. fresh preview). In production the bounds auto-fit
// around real pins, so this only shows on first run / empty days.
const FALLBACK_CENTER = [40.0379, -76.3055];
const FALLBACK_ZOOM = 10;

// Small orange pin used for the landing-page preview. Distinct from
// MapView's full pin renderer (which carries category emoji + status
// pulse + Pro logo logic) because at this size we want a clean,
// uniform marker — the goal is to communicate "many trucks, spread
// across an area", not to deep-link to a specific one.
const _ICON = (() => {
  const html = renderToStaticMarkup(
    <div className="landing-mini-pin">
      <div className="landing-mini-pin-body" />
      <div className="landing-mini-pin-glow" />
    </div>
  );
  return L.divIcon({
    html,
    className: "landing-mini-marker",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
})();

/**
 * LandingMiniMap — small interactive Leaflet map embedded on the
 * landing page. Replaces the previous Unsplash hero image. Loads
 * today's truck positions; auto-fits the viewport to the pin cloud;
 * falls back to a centred regional view when no trucks are parked
 * (so the visual never looks broken on a slow day).
 *
 * The map is intentionally low-interaction:
 *   - scroll-zoom disabled (otherwise scrolling the landing page
 *     gets hijacked by the map)
 *   - the whole tile reads as a "tap to open the full map" CTA via
 *     a clickable overlay button
 *
 * Markers are NOT clickable on this preview — clicking anywhere on
 * the map sends the user to /map. That keeps the surface area for
 * mistaken clicks tiny.
 */
export default function LandingMiniMap() {
  const { theme } = useTheme();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  // Location search — geocodes a city/zip and:
  //   1) recenters the preview map so the user sees their area
  //   2) navigates to /map?lat=&lng=&zoom=&label= on the second tap
  // We split the flow into two taps (search -> review -> open) so an
  // accidental enter doesn't yank the user off the landing page.
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  const [override, setOverride] = useState(null); // {lat, lng, label} when set
  const inputRef = useRef(null);

  const runSearch = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const q = (searchQ || "").trim();
    if (q.length < 2 || searching) return;
    setSearching(true);
    setSearchErr(null);
    try {
      const { data } = await api.get("/geocode", { params: { q } });
      if (!data?.found || !Number.isFinite(data.lat) || !Number.isFinite(data.lng)) {
        setSearchErr("Couldn't find that place");
        return;
      }
      // Show a short, friendly label (use the first two display-name
      // segments, e.g. "Lititz, Lancaster County" -> "Lititz, Lancaster County").
      const labelParts = (data.display_name || q).split(",").map((s) => s.trim());
      const label = labelParts.slice(0, 2).join(", ");
      setOverride({ lat: data.lat, lng: data.lng, label });
    } catch {
      setSearchErr("Search failed — try a city or ZIP");
    } finally {
      setSearching(false);
    }
  };

  const clearOverride = () => {
    setOverride(null);
    setSearchQ("");
    setSearchErr(null);
    inputRef.current?.focus();
  };

  useEffect(() => {
    let cancelled = false;
    // Load today's trucks first; fall back to the week if today is
    // empty (e.g. early morning before any owner has posted).
    const load = async () => {
      try {
        let r = await api.get("/trucks/today");
        let data = Array.isArray(r.data) ? r.data : [];
        if (!data.length) {
          r = await api.get("/trucks/upcoming", { params: { when: "week" } });
          data = Array.isArray(r.data) ? r.data : [];
        }
        if (cancelled) return;
        const valid = data
          .filter((t) => typeof t.lat === "number" && typeof t.lng === "number")
          // Cap to 80 pins — more than that makes the mini-map look
          // like an ant colony and adds no info.
          .slice(0, 80);
        setPoints(valid);
      } catch {
        if (!cancelled) setPoints([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Centre + zoom: when the user has searched for a location, prefer
  // those coords (with a city-level zoom). Otherwise: average over the
  // pin cloud or fall back to the regional default.
  const view = useMemo(() => {
    if (override) {
      return { center: [override.lat, override.lng], zoom: 12 };
    }
    if (!points.length) return { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM };
    let sumLat = 0;
    let sumLng = 0;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    for (const p of points) {
      sumLat += p.lat;
      sumLng += p.lng;
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
    const center = [sumLat / points.length, sumLng / points.length];
    const spread = Math.max(maxLat - minLat, maxLng - minLng);
    // Heuristic spread→zoom — empirically tuned for our markets.
    // <0.05° spread (small town) ~ 12; ~0.3° (county) ~ 10; >1° ~ 8.
    const zoom =
      spread < 0.05 ? 12
      : spread < 0.15 ? 11
      : spread < 0.4 ? 10
      : spread < 1.0 ? 9
      : 8;
    return { center, zoom };
  }, [points]);



  return (
    <div
      className="relative h-64 lg:h-80 rounded-2xl overflow-hidden border border-border bg-card"
      data-testid="landing-mini-map"
    >
      <MapContainer
        key={`${view.center[0].toFixed(3)}-${view.center[1].toFixed(3)}-${view.zoom}`}
        center={view.center}
        zoom={view.zoom}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ width: "100%", height: "100%" }}
      >
        <GlBasemap dark={theme === "dark"} key={theme} />
        {points.map((p, i) => (
          <Marker
            key={p.id || `${p.lat}-${p.lng}-${i}`}
            position={[p.lat, p.lng]}
            icon={_ICON}
          />
        ))}
      </MapContainer>

      {/* Top-left chip: how many trucks are on the map right now. The
          chip is hidden once a search has narrowed the map — the search
          form takes priority on small screens to avoid overlap. */}
      {!override && (
        <div className="absolute top-3 left-3 z-[400] inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-card/95 border border-border backdrop-blur shadow-sm text-xs font-semibold">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          {loading
            ? "Loading…"
            : points.length > 0
              ? `${points.length} truck${points.length === 1 ? "" : "s"} on the map`
              : "Live food-truck map"}
        </div>
      )}

      {/* Location search overlay — top-center. Geocodes city/ZIP and
          recenters the preview tile, then the bottom CTA carries the
          coords through to /map. */}
      <form
        onSubmit={runSearch}
        className="absolute top-3 right-3 z-[500] flex items-center gap-1.5"
        data-testid="landing-mini-map-search-form"
      >
        <div className="relative">
          <MagnifyingGlass
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
          />
          <input
            ref={inputRef}
            type="text"
            value={searchQ}
            onChange={(e) => { setSearchQ(e.target.value); setSearchErr(null); }}
            placeholder="City or ZIP…"
            aria-label="Search trucks by city or ZIP"
            className="h-9 w-[150px] sm:w-[200px] pl-9 pr-3 rounded-full text-sm bg-card/95 border border-border backdrop-blur shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            data-testid="landing-mini-map-search-input"
            onKeyDown={(e) => {
              // Stop the parent <Link> overlay from swallowing Enter.
              if (e.key === "Enter") e.stopPropagation();
            }}
          />
        </div>
        <button
          type="submit"
          disabled={searching || (searchQ || "").trim().length < 2}
          className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow-sm hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="landing-mini-map-search-submit"
        >
          {searching ? "…" : "Find"}
        </button>
      </form>
      {searchErr && (
        <div
          className="absolute top-14 right-3 z-[500] px-3 py-1 rounded-md bg-destructive text-destructive-foreground text-xs shadow-sm"
          data-testid="landing-mini-map-search-error"
        >
          {searchErr}
        </div>
      )}
      {override && (
        <div
          className="absolute top-14 right-3 z-[500] flex items-center gap-1.5 px-2 h-7 rounded-full bg-card/95 border border-border backdrop-blur shadow-sm text-[11px] font-semibold max-w-[260px]"
          data-testid="landing-mini-map-active-location"
        >
          <MapPin weight="fill" className="h-3 w-3 text-primary flex-none" />
          <span className="truncate">{override.label}</span>
          <button
            type="button"
            onClick={clearOverride}
            className="h-5 w-5 rounded-full hover:bg-border grid place-items-center text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
            data-testid="landing-mini-map-clear-location"
          >
            <X weight="bold" className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Full-tile CTA — clicking anywhere on the map opens /map. The
          .landing-mini-marker pin icons sit on top of this in z-order
          but the overlay still captures clicks on empty tiles. When a
          location override is set we carry the coords through so the
          full map opens on the searched area. */}
      <Link
        to={
          override
            ? `/map?lat=${override.lat.toFixed(5)}&lng=${override.lng.toFixed(5)}&zoom=12&label=${encodeURIComponent(override.label)}`
            : "/map"
        }
        aria-label={override ? `Open the live map for ${override.label}` : "Open the live map"}
        data-testid="landing-mini-map-cta"
        className="absolute inset-0 z-[300] block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={(e) => {
          // Let the search form (z-500) capture clicks on the input.
          // Anywhere else on the tile -> navigate.
          // (z-index alone covers this; this is just an extra guard
          // for the input edge cases on some mobile browsers.)
          if (e.target.closest("[data-testid='landing-mini-map-search-form']")) {
            e.preventDefault();
          }
        }}
      >
        <span className="sr-only">{override ? `Open the live map for ${override.label}` : "Open the live map"}</span>
      </Link>

      {/* Bottom-right pill — explicit "Open" affordance for users who
          don't realize the whole tile is clickable. */}
      <div className="absolute bottom-3 right-3 z-[400] pointer-events-none inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-md">
        <MapPin weight="duotone" className="h-4 w-4" />
        {override ? `Open ${override.label.split(",")[0]}` : "Open the live map"}
        <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  );
}
